import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  CalendarCheck, Clock, Swords, Heart, ShieldAlert, CheckCircle2, ChevronRight, User,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pinned "Today's Status" card for Player / Parent dashboards.
 * Read-only summary that surfaces the 4 things they care about most,
 * all drawn from existing endpoints:
 *   - Athlete status   (name, squad, availability)
 *   - Next training    (from /athletes/:id/360 · training.upcoming_sessions OR /dashboard/kpis)
 *   - Latest match     (from /athletes/:id/360 · performance.recent_matches[0])
 *   - Injury status    (from /athletes/:id/360 · medical + performance_snapshot.injury_status)
 *
 * Uses only existing endpoints. No new APIs, no new data models.
 */
const TodayStatusCard = ({ role = 'Player' }) => {
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const listRes = await axios.get(`${API}/athletes`);
        const first = Array.isArray(listRes.data) && listRes.data.length > 0 ? listRes.data[0] : null;
        if (!first) {
          if (!cancelled) setLoading(false);
          return;
        }
        if (!cancelled) setAthlete(first);
        const dashRes = await axios.get(`${API}/athletes/${first.id}/360`);
        if (!cancelled) setSummary(dashRes.data || {});
      } catch (err) {
        // Silent fail — degrade to a compact name-only card.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card
        className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-6"
        data-testid="today-status-loading"
      >
        <CardContent className="p-4">
          <div className="h-4 w-40 bg-gray-100 dark:bg-gray-700 rounded animate-pulse mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-lg bg-gray-50 dark:bg-gray-700/50 animate-pulse" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!athlete) return null;

  // Derive the 4 summary values from existing data.
  const snap = summary?.performance_snapshot || {};
  const perf = summary?.performance || {};
  const trn = summary?.training || {};
  const med = summary?.medical || {};

  const injuryStatus = snap.injury_status; // "Fit", "Injured", "In Recovery", etc.
  const isInjured = (med.active_injuries || 0) > 0 || (injuryStatus && injuryStatus.toLowerCase() !== 'fit');
  const availLabel = isInjured ? (injuryStatus || 'Injured') : 'Available';
  const availTone = isInjured ? 'rose' : 'emerald';

  // Next training: prefer training.next_session, fall back to recent_sessions[0] if future-dated,
  // otherwise show "None scheduled".
  const nextSession =
    trn.next_session ||
    (Array.isArray(trn.upcoming_sessions) && trn.upcoming_sessions[0]) ||
    null;

  // Latest match from performance.recent_matches (already sorted newest-first by backend)
  const latestMatch = Array.isArray(perf.recent_matches) && perf.recent_matches.length > 0
    ? perf.recent_matches[0]
    : null;

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } catch {
      return d;
    }
  };

  const viewer = role === 'Parent' ? "child's" : 'your';

  return (
    <Card
      className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-6 overflow-hidden"
      data-testid="today-status-card"
    >
      <CardContent className="p-4 sm:p-5">
        {/* Header: athlete identity pill + viewer hint */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <User className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
              Today · {viewer} status
            </p>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
              {athlete.full_name}
              <span className="text-xs text-gray-400 ml-2 font-normal">
                {athlete.squad ? `${athlete.squad}` : ''}{athlete.position ? ` · ${athlete.position}` : ''}
              </span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Tile 1: Availability / Status */}
          <StatusTile
            testId="status-tile-availability"
            icon={isInjured ? ShieldAlert : CheckCircle2}
            label="Availability"
            primary={availLabel}
            secondary={isInjured ? `${med.active_injuries || 1} active` : 'Cleared for training'}
            tone={availTone}
            onClick={() => navigate(`/athlete/${athlete.id}/360`)}
          />

          {/* Tile 2: Next training */}
          <StatusTile
            testId="status-tile-training"
            icon={CalendarCheck}
            label="Next Training"
            primary={nextSession?.title || (trn.attendance_rate !== undefined ? `${trn.attendance_rate}% attendance` : 'None scheduled')}
            secondary={nextSession?.date
              ? `${fmtDate(nextSession.date)}${nextSession.time ? ` · ${String(nextSession.time).slice(0,5)}` : ''}`
              : `${trn.total_sessions_attended || 0} sessions attended`}
            tone="blue"
            onClick={() => navigate('/my-training')}
          />

          {/* Tile 3: Latest match */}
          <StatusTile
            testId="status-tile-match"
            icon={Swords}
            label="Latest Match"
            primary={latestMatch ? `vs ${latestMatch.opponent || 'Unknown'}` : 'No matches yet'}
            secondary={latestMatch
              ? `${fmtDate(latestMatch.match_date)}${latestMatch.rating ? ` · rating ${latestMatch.rating}` : ''}`
              : 'Match log will appear here'}
            tone="purple"
            onClick={() => navigate('/my-performance')}
          />

          {/* Tile 4: Injury status */}
          <StatusTile
            testId="status-tile-injury"
            icon={Heart}
            label="Injury Status"
            primary={isInjured ? (injuryStatus || 'Active injury') : 'No active injury'}
            secondary={med.total_injuries !== undefined
              ? `${med.total_injuries} lifetime${med.active_injuries ? ` · ${med.active_injuries} active` : ''}`
              : ''}
            tone={isInjured ? 'rose' : 'emerald'}
            onClick={() => navigate(`/athlete/${athlete.id}/360`)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const TONES = {
  emerald: {
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
    primary: 'text-gray-900 dark:text-gray-100',
  },
  blue: {
    iconBg: 'bg-blue-50 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400',
    primary: 'text-gray-900 dark:text-gray-100',
  },
  purple: {
    iconBg: 'bg-purple-50 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400',
    primary: 'text-gray-900 dark:text-gray-100',
  },
  rose: {
    iconBg: 'bg-rose-50 dark:bg-rose-900/30',
    icon: 'text-rose-600 dark:text-rose-400',
    primary: 'text-rose-700 dark:text-rose-300',
  },
};

const StatusTile = ({ icon: Icon, label, primary, secondary, tone, onClick, testId }) => {
  const s = TONES[tone] || TONES.emerald;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group text-left p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 hover:bg-white dark:hover:bg-gray-800 hover:shadow-sm transition-all flex items-start gap-3"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.iconBg}`}>
        <Icon className={`w-4 h-4 ${s.icon}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
          {label}
        </p>
        <p className={`text-sm font-semibold truncate ${s.primary}`}>{primary}</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{secondary}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

export default TodayStatusCard;
